import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabaseClient';
import { valoreWebSocket, ValorePriceData } from '../services/valoreWebSocket';
import { LISTED_CFD_INSTRUMENTS, getListedCfdInstrument } from '../../supabase/functions/_shared/cfdInstruments';

export interface MarketDataItem {
  id?: string;
  symbol: string;
  price: number;
  volume_24h?: number;
  change_24h?: number;
  timestamp: string;
  high_price_24h?: number;
  low_price_24h?: number;
  market_cap?: number;
  funding_rate?: number;
  open_interest?: number;
  bid_price?: number;
  ask_price?: number;
  updated_at?: string;
}

interface MarketDataContextType {
  marketData: MarketDataItem[];
  snapshotData: MarketDataItem[];
  isConnected: boolean;
  error: string | null;
  getMarketDataBySymbol: (symbol: string) => MarketDataItem | null;
  getPriceBySymbol: (symbol: string) => number;
  getSnapshotPriceBySymbol: (symbol: string) => number;
  refreshSnapshot: () => void;
  lastSnapshotTime: number;
}

const MarketDataContext = createContext<MarketDataContextType | undefined>(undefined);

interface MarketDataProviderProps {
  children: React.ReactNode;
}

const isCryptoSymbol = (symbol: string): boolean => {
  const cryptoSuffixes = ['USDT', 'BUSD', 'USDC', 'DAI', 'TUSD'];
  const upperSymbol = symbol.toUpperCase();
  return cryptoSuffixes.some(suffix => upperSymbol.endsWith(suffix) && upperSymbol.length > suffix.length);
};

const isForexSymbol = (symbol: string): boolean => {
  const forexPairs = [
    'EURUSD', 'GBPUSD', 'AUDUSD', 'NZDUSD', 'USDJPY', 'USDCHF', 'USDCAD',
    'EURJPY', 'EURGBP', 'EURAUD', 'EURCHF', 'EURCAD', 'EURNZD',
    'GBPJPY', 'GBPCHF', 'GBPAUD', 'GBPCAD', 'GBPNZD',
    'AUDJPY', 'AUDCHF', 'AUDCAD', 'AUDNZD',
    'NZDJPY', 'NZDCHF', 'NZDCAD',
    'CADJPY', 'CADCHF', 'CHFJPY',
    'XAUUSD', 'XAGUSD', 'XPTUSD', 'XPDUSD',
    'WTICOUSD', 'BCOUSD', 'NATGASUSD'
  ];
  return forexPairs.includes(symbol.toUpperCase().replace('/', ''));
};

export const MarketDataProvider: React.FC<MarketDataProviderProps> = ({ children }) => {
  const [dbData, setDbData] = useState<MarketDataItem[]>([]);
  const [valoreData, setValoreData] = useState<MarketDataItem[]>([]);
  const [snapshotData, setSnapshotData] = useState<MarketDataItem[]>([]);
  const [lastSnapshotTime, setLastSnapshotTime] = useState<number>(Date.now());
  const [isConnected, setIsConnected] = useState(false);
  const [valoreConnected, setValoreConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizeSymbol = (symbol: string): string => {
    return symbol.replace('/', '');
  };

  const marketData = useMemo(() => {
    const symbolMap = new Map<string, MarketDataItem>();
    const normalizedDbMap = new Map<string, MarketDataItem>();

    // First add DB data as fallback
    dbData.forEach(item => {
      symbolMap.set(item.symbol, item);
      normalizedDbMap.set(normalizeSymbol(item.symbol), item);
    });

    // Then override with Valore WebSocket real-time prices
    valoreData.forEach(valoreItem => {
      const normalizedValoreSymbol = normalizeSymbol(valoreItem.symbol);
      const existingDbItem = symbolMap.get(valoreItem.symbol) || normalizedDbMap.get(normalizedValoreSymbol);

      if (existingDbItem) {
        if (getListedCfdInstrument(valoreItem.symbol) && Date.parse(existingDbItem.timestamp) > Date.parse(valoreItem.timestamp)) return;
        const mergedItem = {
          ...existingDbItem,
          price: valoreItem.price,
          bid_price: valoreItem.bid_price,
          ask_price: valoreItem.ask_price,
          timestamp: valoreItem.timestamp,
          updated_at: valoreItem.updated_at
        };
        symbolMap.set(existingDbItem.symbol, mergedItem);
      } else {
        symbolMap.set(valoreItem.symbol, valoreItem);
      }
    });

    const result = Array.from(symbolMap.values());
    return result;
  }, [dbData, valoreData]);

  // Convert Valore price data to MarketDataItem
  const convertValorePriceToMarketData = useCallback((symbol: string, priceData: ValorePriceData): MarketDataItem => {
    return {
      symbol,
      price: priceData.mid,
      bid_price: priceData.bid,
      ask_price: priceData.ask,
      timestamp: new Date(priceData.timestamp).toISOString(),
      updated_at: new Date(priceData.timestamp).toISOString(),
      volume_24h: 0,
      change_24h: 0,
      high_price_24h: 0,
      low_price_24h: 0,
      market_cap: 0,
      funding_rate: 0,
      open_interest: 0
    };
  }, []);

  // Handle Valore WebSocket updates
  useEffect(() => {
    // Connect to Valore WebSocket
    valoreWebSocket.connect();

    // Subscribe to price updates
    const unsubscribePrices = valoreWebSocket.subscribe((prices: Map<string, ValorePriceData>) => {
      const newValoreData: MarketDataItem[] = [];

      prices.forEach((priceData, symbol) => {
        // Only include non-crypto symbols from Valore
        if (!isCryptoSymbol(symbol)) {
          newValoreData.push(convertValorePriceToMarketData(symbol, priceData));
        }
      });

      setValoreData(newValoreData);
    });

    // Subscribe to connection status
    const unsubscribeStatus = valoreWebSocket.onStatusChange((status) => {
      setValoreConnected(status === 'connected');
    });

    // Periodic forced refresh to ensure UI updates
    const refreshInterval = setInterval(() => {
      const currentPrices = valoreWebSocket.getAllPrices();
      if (currentPrices.size > 0) {
        const newValoreData: MarketDataItem[] = [];
        currentPrices.forEach((priceData, symbol) => {
          if (!isCryptoSymbol(symbol)) {
            newValoreData.push(convertValorePriceToMarketData(symbol, priceData));
          }
        });
        setValoreData(newValoreData);
      }
    }, 1000);

    return () => {
      unsubscribePrices();
      unsubscribeStatus();
      clearInterval(refreshInterval);
      valoreWebSocket.disconnect();
    };
  }, [convertValorePriceToMarketData]);


  // Get market data by symbol - prioritizes Valore WebSocket over DB fallback
  const getMarketDataBySymbol = useCallback((symbol: string): MarketDataItem | null => {
    const normalizedSymbol = normalizeSymbol(symbol);

    const dbItem = dbData.find(item =>
      item.symbol === symbol || normalizeSymbol(item.symbol) === normalizedSymbol
    );
    const valoreItem = valoreData.find(item =>
      item.symbol === symbol || normalizeSymbol(item.symbol) === normalizedSymbol
    );

    // Prioritize Valore WebSocket real-time data, fall back to DB
    if (valoreItem && dbItem) {
      if (getListedCfdInstrument(symbol) && Date.parse(dbItem.timestamp) > Date.parse(valoreItem.timestamp)) return dbItem;
      return {
        ...dbItem,
        price: valoreItem.price,
        bid_price: valoreItem.bid_price,
        ask_price: valoreItem.ask_price,
        timestamp: valoreItem.timestamp,
        updated_at: valoreItem.updated_at
      };
    }

    return valoreItem || dbItem || null;
  }, [dbData, valoreData]);

  // Get price by symbol (real-time)
  const getPriceBySymbol = useCallback((symbol: string): number => {
    const item = getMarketDataBySymbol(symbol);
    return item?.price || 0;
  }, [getMarketDataBySymbol]);

  // Get snapshot price by symbol (stable)
  const getSnapshotPriceBySymbol = useCallback((symbol: string): number => {
    const item = snapshotData.find(item => item.symbol === symbol);
    return item?.price || 0;
  }, [snapshotData]);

  // Refresh snapshot with current market data
  const refreshSnapshot = useCallback(() => {
    setSnapshotData([...marketData]);
    setLastSnapshotTime(Date.now());
  }, [marketData]);

  const loadMarketData = useCallback(async () => {
    try {
      let allData: any[] = [];
      let page = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error: fetchError, count } = await supabase
          .from('market_data')
          .select('*', { count: 'exact' })
          .order('symbol')
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (fetchError) {
          setError('Failed to load market data');
          return;
        }

        if (data) {
          allData = [...allData, ...data];
          hasMore = data.length === pageSize && allData.length < (count || 0);
          page++;
        } else {
          hasMore = false;
        }
      }

      if (allData.length > 0) {
        const formattedData = allData.map(item => ({
          symbol: item.symbol,
          price: parseFloat(item.price) || 0,
          volume_24h: parseFloat(item.volume_24h) || 0,
          change_24h: parseFloat(item.change_24h) || 0,
          timestamp: item.timestamp || new Date().toISOString(),
          high_price_24h: parseFloat(item.high_price_24h) || 0,
          low_price_24h: parseFloat(item.low_price_24h) || 0,
          market_cap: parseFloat(item.market_cap) || 0,
          funding_rate: parseFloat(item.funding_rate) || 0,
          open_interest: parseFloat(item.open_interest) || 0,
          bid_price: parseFloat(item.bid_price) || 0,
          ask_price: parseFloat(item.ask_price) || 0,
          updated_at: item.updated_at || new Date().toISOString()
        }));
        setDbData(formattedData);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load market data');
    }
  }, []);

  // Load initial data from database (fallback only, no real-time updates)
  useEffect(() => {
    loadMarketData();
  }, [loadMarketData]);

  // Refresh the VPS-backed database mirror as a fallback for the live stream.
  useEffect(() => {
    let cancelled = false;
    const refreshListedCfds = async () => {
      if (document.visibilityState === 'hidden') return;
      const { data, error: fetchError } = await supabase.from('market_data')
        .select('*').in('symbol', LISTED_CFD_INSTRUMENTS.map(instrument => instrument.symbol));
      if (cancelled || fetchError || !data) return;
      const quotes = data.map(item => ({ ...item, price: Number(item.price) || 0 }));
      setDbData(previous => [...previous.filter(item => !getListedCfdInstrument(item.symbol)), ...quotes]);
    };
    const interval = setInterval(() => { void refreshListedCfds(); }, 30_000);
    document.addEventListener('visibilitychange', refreshListedCfds);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshListedCfds);
    };
  }, []);

  // Update snapshot when marketData changes
  useEffect(() => {
    if (marketData.length > 0) {
      setSnapshotData([...marketData]);
      setLastSnapshotTime(Date.now());
    }
  }, [marketData]);

  // Update overall connection status based on Valore WebSocket only
  useEffect(() => {
    setIsConnected(valoreConnected);
  }, [valoreConnected]);

  const contextValue: MarketDataContextType = {
    marketData,
    snapshotData,
    isConnected,
    error,
    getMarketDataBySymbol,
    getPriceBySymbol,
    getSnapshotPriceBySymbol,
    refreshSnapshot,
    lastSnapshotTime
  };

  return (
    <MarketDataContext.Provider value={contextValue}>
      {children}
    </MarketDataContext.Provider>
  );
};

export const useMarketData = (): MarketDataContextType => {
  const context = useContext(MarketDataContext);
  if (context === undefined) {
    throw new Error('useMarketData must be used within a MarketDataProvider');
  }
  return context;
};
